// Geometry for menus that are portalled to <body> and positioned by hand
// against the button that opened them.
//
// Portalling escapes the clipping and stacking contexts of the scrollable
// panes these menus live in, at the cost of having to place them ourselves.
// The two rules that are easy to get wrong, and that #11179 got wrong on the
// entity Documents tab:
//
//   1. A portalled menu is `position: fixed`, and `getBoundingClientRect()` is
//      already viewport-relative. Adding `window.scrollX/scrollY` on top of
//      those coordinates drifts the menu by however far the page is scrolled.
//   2. A menu that flips above its button has to be flipped by its OWN height.
//      Using the CSS max-height ceiling instead strands short menus that far
//      above the button -- worst for the shortest menus, which is why an org
//      with few document groups saw the menu furthest from where it belonged.

export interface AnchoredMenuGeometry {
  // Viewport coordinates, for a `position: fixed` element.
  top: number;
  left: number;
  // What the menu is allowed to grow to on the side it was placed. Smaller
  // than the content when the viewport is cramped, so the menu scrolls its
  // own contents instead of hanging off an edge.
  maxHeight: number;
}

export interface AnchoredMenuOptions {
  // The trigger's viewport rect: only the edges the placement reads, so a
  // pointer can stand in for a trigger as a zero-size rect.
  anchor: Pick<DOMRect, "top" | "bottom" | "right">;
  // The menu's full content height, borders included.
  contentHeight: number;
  menuWidth: number;
  viewportWidth: number;
  viewportHeight: number;
  // Space left between the menu and its trigger.
  gap: number;
  // Space kept between the menu and the edges of the window.
  edgePadding: number;
  // The menu's own ceiling, matching its CSS max-height.
  maxHeight: number;
  // Never shrink below this, even in a viewport with no room: a menu clamped
  // to a few pixels is worse than one that overhangs slightly.
  minHeight: number;
}

// Place a menu adjacent to its trigger: below it by preference, above it when
// below doesn't fit and above fits better, and never past a window edge.
export const computeAnchoredMenuPosition = ({
  anchor,
  contentHeight,
  menuWidth,
  viewportWidth,
  viewportHeight,
  gap,
  edgePadding,
  maxHeight,
  minHeight,
}: AnchoredMenuOptions): AnchoredMenuGeometry => {
  const spaceBelow = viewportHeight - anchor.bottom - gap - edgePadding;
  const spaceAbove = anchor.top - gap - edgePadding;

  const desiredHeight = Math.min(contentHeight, maxHeight);
  // Only flip when below genuinely can't hold the menu AND above is the
  // roomier side -- flipping into an even tighter space helps nobody.
  const placeAbove = desiredHeight > spaceBelow && spaceAbove > spaceBelow;

  const available = placeAbove ? spaceAbove : spaceBelow;
  const height = Math.min(desiredHeight, Math.max(available, minHeight));

  // Anchored to the menu's real height, so the gap to the button is the same
  // whichever side it lands on.
  let top = placeAbove ? anchor.top - gap - height : anchor.bottom + gap;
  // Both clamps together, in this order: pull an overhanging menu up off the
  // bottom edge first, then refuse to push it past the top edge. The top wins
  // when the window is too short for the menu either way, because the top of
  // the list is the part worth keeping reachable.
  top = Math.min(top, viewportHeight - edgePadding - height);
  top = Math.max(edgePadding, top);

  // Right-aligned to the trigger, the way a row-end overflow menu reads,
  // then pulled inside the window if that would overhang.
  let left = anchor.right - menuWidth;
  left = Math.min(left, viewportWidth - menuWidth - edgePadding);
  left = Math.max(edgePadding, left);

  return { top, left, maxHeight: height };
};

// A menu opened at the pointer (a right-click menu) rather than against a
// button: its top-left corner sits on the pointer when the menu fits below
// it, it opens upward by its own height when it does not (#14681), and it
// caps itself to the roomier side, scrolling its own entries, when neither
// side holds it.
const POINTER_MENU_EDGE_PADDING = 8;
// Small enough for a window a few entries tall, large enough that a capped
// menu still shows a handful of entries to scroll through.
const POINTER_MENU_MIN_HEIGHT = 120;

export interface PointerMenuOptions {
  // The pointer, in viewport coordinates (a mouse event's clientX/clientY).
  x: number;
  y: number;
  // The menu's full content height, borders included.
  contentHeight: number;
  menuWidth: number;
  viewportWidth: number;
  viewportHeight: number;
  // False for a submenu, whose top belongs level with the entry that opened
  // it: it never flips above that entry, it slides up only as far as it must
  // to stay inside the window, and it caps to the window when taller.
  flip?: boolean;
}

export const computePointerMenuPosition = ({
  x,
  y,
  contentHeight,
  menuWidth,
  viewportWidth,
  viewportHeight,
  flip = true,
}: PointerMenuOptions): AnchoredMenuGeometry => {
  // The left edge stays on the pointer (the shared helper right-aligns to
  // its trigger, a row-end menu's reading), pulled inside the window when the
  // menu would overhang its right edge.
  const left = Math.max(
    POINTER_MENU_EDGE_PADDING,
    Math.min(x, viewportWidth - menuWidth - POINTER_MENU_EDGE_PADDING)
  );
  if (!flip) {
    const maxHeight = Math.min(
      contentHeight,
      viewportHeight - 2 * POINTER_MENU_EDGE_PADDING
    );
    const top = Math.max(
      POINTER_MENU_EDGE_PADDING,
      Math.min(y, viewportHeight - POINTER_MENU_EDGE_PADDING - maxHeight)
    );
    return { top, left, maxHeight };
  }
  const vertical = computeAnchoredMenuPosition({
    // The pointer as a zero-height trigger.
    anchor: { top: y, bottom: y, right: x },
    contentHeight,
    menuWidth,
    viewportWidth,
    viewportHeight,
    gap: 0,
    edgePadding: POINTER_MENU_EDGE_PADDING,
    maxHeight: viewportHeight - 2 * POINTER_MENU_EDGE_PADDING,
    minHeight: POINTER_MENU_MIN_HEIGHT,
  });
  return { top: vertical.top, left, maxHeight: vertical.maxHeight };
};

// The height a menu's box needs to hold its content without scrolling: the
// content's height plus the box's top and bottom borders, all in fractional
// CSS pixels, rounded up. Summing the rounded integer sizes (offsetHeight)
// under a fractional device pixel ratio comes out a fraction of a pixel
// short, and a box capped there shows a stray scrollbar (#14681).
export const menuBoxHeightFor = (
  contentHeight: number,
  borderTop: number,
  borderBottom: number
): number => Math.ceil(contentHeight + borderTop + borderBottom);

// The height a menu WANTS to be: its full content plus its own border,
// however far its max-height is currently capping the box. scrollHeight is
// the uncapped content, so this reads the same before and after the cap
// lands -- which is what lets a menu measure itself on the frame it opens
// and flip by its real height rather than by its ceiling (rule 2 above).
export const measureMenuContentHeight = (menu: HTMLElement): number =>
  menu.scrollHeight + (menu.offsetHeight - menu.clientHeight);

// Walk up the DOM to find the nearest overflow:auto/scroll ancestor.
// Used so dropdown positioning logic can detect when the anchor cell
// has scrolled outside its own scrollable container (e.g. the RMS grid)
// and close the dropdown instead of leaving it floating in the page.
export const findScrollableAncestor = (el: HTMLElement): HTMLElement | null => {
  let cur: HTMLElement | null = el.parentElement;
  while (cur && cur !== document.body) {
    const style = window.getComputedStyle(cur);
    const overflowY = style.overflowY;
    const overflowX = style.overflowX;
    if (
      overflowY === "auto" ||
      overflowY === "scroll" ||
      overflowX === "auto" ||
      overflowX === "scroll"
    ) {
      return cur;
    }
    cur = cur.parentElement;
  }
  return null;
};

// True when the anchor has scrolled clear of the pane it lives in, so a menu
// attached to it has nothing left to point at.
export const isAnchorOutsideScrollParent = (
  anchor: DOMRect,
  scrollParent: HTMLElement
): boolean => {
  const parent = scrollParent.getBoundingClientRect();
  return (
    anchor.bottom < parent.top ||
    anchor.top > parent.bottom ||
    anchor.right < parent.left ||
    anchor.left > parent.right
  );
};
